import React, { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { Validator } from "../../models/Validator";
import { IndexerService } from "../../services/indexer.service";
import { useNavigate, useParams } from "react-router-dom";
import { Node, RelatedNode } from "../../models/Node";
import styles from "./ValidatorDetailsPage.module.scss";
import {
  Box,
  Group,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { LabelValueStack } from "../../components/labelValueStack/LabelValueStack.component";
import CopyTextComponent from "../../components/copyText/CopyText.component";
import {
  formatAddress,
  formatAmountWithLabel,
  formatNumber,
} from "../../utils/formatter";
import {
  calculateDateDifference,
  getDaysRemaining,
} from "../../components/transactionDetailsTable/TransactionDetailsTable.component";
import ToggleUnixTime from "../../components/toggleUnixTime/ToggleUnixTime.component";
import CurrentDelegationsTable from "../../components/tables/CurrentDelegationsTable";
import { IconHelpSquareRounded } from "@tabler/icons-react";
import NodeHistoryTable from "../../components/tables/NodeHistoryTable";
import Button from "../../components/Button/Button.component";
import { OnlineIndicator } from "../../components/onlineIndicator/OnlineIndicator";
import { IconExternalLink } from "@tabler/icons-react";
import { UNKNOWN_NAME } from "../../constants";

export default function ValidatorDetailsPage(): ReactElement | null {
  const { nodeId } = useParams();
  const navigate = useNavigate();

  const { data: validator } = useQuery({
    queryKey: [QueryKey.validator, nodeId],
    queryFn: (): Promise<Validator> => IndexerService.getValidator(nodeId!),
    enabled: !!nodeId,
  });
  const { data: node } = useQuery({
    queryKey: [QueryKey.node, nodeId],
    queryFn: (): Promise<Node | null> => IndexerService.getNode(nodeId!),
    enabled: !!nodeId,
  });

  let copyAddress = <></>;
  if (nodeId) {
    copyAddress = (
      <CopyTextComponent
        text={formatAddress(nodeId, 18, true)}
        value={nodeId}
        weight={"bold"}
        primaryColor={true}
        size={"24px"}
      ></CopyTextComponent>
    );
  }

  let durationAndDaysRemainingValue = <></>;
  if (validator) {
    const duration: number = calculateDateDifference(
      new Date(validator.startTime),
      new Date(validator.endTime),
      true
    );
    durationAndDaysRemainingValue = (
      <>
        <span>{Math.floor(duration)}</span>
        <span style={{ fontWeight: 400 }}>
          &nbsp;
          {Math.floor(duration) === 1 ? "day" : "days"}
        </span>
        <span>&nbsp;/&nbsp;</span>
        {getDaysRemaining(validator.endTime, true, true)}
        <span style={{ fontWeight: 400 }}> &nbsp;remaining</span>
      </>
    );
  }

  return (
    <>
      <div className={styles.content}>
        <Group justify={"space-between"} align={"center"} mb={"1.5rem"}>
          <Box w={"fit-content"}>
            <h2>Overview</h2>
          </Box>

          <Button
            addStyles={["status"]}
            onClick={() => navigate("/validators")}
          >
            View Validators
          </Button>
        </Group>

        {validator && validator.selfBond > 0 ? (
          <>
            {/*FIRST ROW*/}
            <Group className={`${styles.backgroundColorRow} ${styles.primary}`}>
              <div className={styles.placeholder}>
                <LabelValueStack
                  label={"Name"}
                  value={node?.name ?? UNKNOWN_NAME}
                  primary={true}
                  primaryLabel={true}
                  bottomOffset={-4}
                  size={"xl"}
                />
              </div>
              <Group justify={"space-between"} w={"100%"}>
                <LabelValueStack
                  label={"Node ID"}
                  value={
                    nodeId && (
                      <Group gap={0}>
                        <>{copyAddress}</>
                        <Tooltip
                          label={"Initial Transaction"}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            size={24}
                            color="var(--mid-dark)"
                            variant="transparent"
                            className={styles.actionIcon}
                            onClick={() => {
                              navigate(`/tx/${validator?.txID}`);
                            }}
                          >
                            <IconExternalLink size={24} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )
                  }
                  primary={true}
                  primaryLabel={true}
                  size={"xl"}
                  bottomOffset={-5}
                />
                <LabelValueStack
                  label={"Fee"}
                  value={
                    <span>
                      {validator
                        ? formatNumber(
                            validator.feePercentage / 1e4,
                            false,
                            1
                          ) + "%"
                        : "n/a"}
                    </span>
                  }
                  primary={false}
                  primaryLabel={false}
                  size={"lg"}
                  bottomOffset={-1}
                />
              </Group>
            </Group>

            {/*SECOND ROW*/}
            <Group className={`${styles.backgroundColorRow}`} mt={10}>
              <div className={styles.placeholder}>
                <LabelValueStack
                  label={"Start time"}
                  value={<ToggleUnixTime time={validator.startTime} />}
                  primary={false}
                  size={"lg"}
                  bottomOffset={-2}
                />
              </div>
              <Group w={"100%"} className={styles.insideRowWrapper}>
                <div className={styles.placeholder}>
                  <LabelValueStack
                    label={"End time"}
                    value={<ToggleUnixTime time={validator.endTime} />}
                    primary={false}
                    size={"lg"}
                    bottomOffset={-2}
                  />
                </div>
                <LabelValueStack
                  label={"Duration & Days remaining"}
                  value={durationAndDaysRemainingValue}
                  primary={false}
                  size={"lg"}
                  bottomOffset={-2}
                />{" "}
                <LabelValueStack
                  label={"Online"}
                  value={
                    <Group align={"center"}>
                      <span>{validator.online ? "Yes" : "No"}</span>
                      <OnlineIndicator online={validator.online} />
                    </Group>
                  }
                  primary={false}
                  size={"lg"}
                  bottomOffset={-2}
                />
              </Group>
            </Group>

            {/* RELATED NODES */}
            <Box mb={"60px"} mt={"4px"}>
              {node && (
                <>
                  {node.relatedNodes.map((relatedNode: RelatedNode) => (
                    <Group
                      key={relatedNode.nodeId}
                      className={`${styles.relatedNodeRow} ${styles.transparent}`}
                    >
                      <div className={styles.placeholder}>
                        <LabelValueStack
                          label={"Related node Name"}
                          size={"sm"}
                          bottomOffset={-2}
                          value={
                            <span style={{ fontWeight: 400, fontSize: 16 }}>
                              {relatedNode.name}
                            </span>
                          }
                          primary={false}
                        />
                      </div>
                      <LabelValueStack
                        label={"Related node ID"}
                        size={"sm"}
                        bottomOffset={-5}
                        value={
                          <CopyTextComponent
                            text={formatAddress(relatedNode.nodeId, 24, true)}
                            link={`/validator/${relatedNode.nodeId}`}
                            value={relatedNode.nodeId}
                            // weight={"bold"}
                            primaryColor={false}
                          ></CopyTextComponent>
                        }
                        primary={false}
                      />
                    </Group>
                  ))}
                </>
              )}
            </Box>
          </>
        ) : (
          <>
            {validator?.selfBond === 0 ? (
              <>
                <Group
                  className={`${styles.backgroundColorRow} ${styles.primary}`}
                >
                  <LabelValueStack
                    label={"Node ID"}
                    value={copyAddress}
                    primary={true}
                    primaryLabel={true}
                    bottomOffset={-4}
                    size={"xl"}
                  />
                  <LabelValueStack
                    label={"Status"}
                    value={"Offline"}
                    primary={true}
                    primaryLabel={true}
                    size={"xl"}
                    bottomOffset={-5}
                  />
                </Group>
              </>
            ) : (
              <Stack>
                <Skeleton width={"100%"} height={"80px"} />
                <Skeleton width={"100%"} height={"80px"} />
                <Skeleton width={"100%"} height={"80px"} />
              </Stack>
            )}
          </>
        )}

        {validator && validator.selfBond === 0 ? (
          <>
            {/*Do not display data for this Node. It should be inactive with incomplete data.*/}
          </>
        ) : (
          <>
            <Box mb={"1.5rem"} w={"fit-content"}>
              <h2>Delegations</h2>
            </Box>

            {validator ? (
              <>
                <Group gap={10}>
                  <Group
                    className={`${styles.backgroundColorRow} ${styles.width75}`}
                    // justify={"space-between"}
                  >
                    <div className={styles.placeholder}>
                      <LabelValueStack
                        label={"Available stake"}
                        value={formatAmountWithLabel(
                          Math.min(3e17, 15 * validator.selfBond),
                          true,
                          2
                        )}
                        primary={false}
                        size={"lg"}
                        bottomOffset={-2}
                      />
                    </div>
                    <div className={styles.placeholder}>
                      <LabelValueStack
                        label={"Total stake"}
                        value={formatAmountWithLabel(
                          validator.totalStake,
                          true,
                          2
                        )}
                        primary={false}
                        size={"lg"}
                        bottomOffset={-2}
                      />
                    </div>
                    <LabelValueStack
                      label={"Free space"}
                      value={formatAmountWithLabel(
                        validator.freeSpace,
                        true,
                        2
                      )}
                      primary={false}
                      size={"lg"}
                      bottomOffset={-2}
                    />
                  </Group>
                </Group>

                <Group
                  className={`${styles.backgroundColorRow} ${styles.width75}`}
                  mt={10}
                >
                  <div className={styles.placeholder}>
                    <LabelValueStack
                      label={"Initial stake"}
                      value={formatAmountWithLabel(validator.selfBond, true, 2)}
                      primary={false}
                      size={"lg"}
                      bottomOffset={-2}
                    />
                  </div>
                  <div className={styles.placeholder}>
                    <LabelValueStack
                      label={"Total delegation"}
                      value={formatAmountWithLabel(
                        validator.totalDelegated,
                        true,
                        2
                      )}
                      primary={false}
                      size={"lg"}
                      bottomOffset={-2}
                    />
                  </div>
                  <LabelValueStack
                    label={"Number of delegations"}
                    value={validator.delegators}
                    primary={false}
                    size={"lg"}
                    bottomOffset={-2}
                  />
                </Group>
              </>
            ) : (
              <Stack>
                <Skeleton width={"100%"} height={"80px"} />
                <Skeleton width={"100%"} height={"80px"} />
                <Skeleton width={"100%"} height={"80px"} />
              </Stack>
            )}

            {nodeId && (
              <Group className={styles.tableRow}>
                <div className={styles.labelContainer}>
                  <div className={styles.textWithHelp}>
                    <Tooltip
                      label={"Complete list of current delegations"}
                      position="top"
                      events={{ hover: true, focus: false, touch: true }}
                    >
                      <IconHelpSquareRounded size={20} />
                    </Tooltip>

                    <Text ta={{ sm: "center", md: "left" }} fw={700}>
                      Current delegations:
                    </Text>
                  </div>
                </div>
                <div className={styles.tableWrapper}>
                  <CurrentDelegationsTable nodeId={nodeId} />
                </div>
              </Group>
            )}

            {nodeId && (
              <Group className={styles.tableRow}>
                <div className={styles.labelContainer}>
                  <div className={styles.textWithHelp}>
                    <Tooltip
                      label={
                        "Node performance in previous epochs. Note: Uptime history data is calculated and available from 182 epoch onwards."
                      }
                      multiline
                      w={220}
                      position="top"
                      events={{ hover: true, focus: false, touch: true }}
                    >
                      <IconHelpSquareRounded size={20} />
                    </Tooltip>

                    <Text ta={{ sm: "center", md: "left" }} fw={700}>
                      Node history:
                    </Text>
                  </div>
                </div>
                <div className={styles.tableWrapper}>
                  <NodeHistoryTable nodeId={nodeId} />
                </div>
              </Group>
            )}
          </>
        )}
      </div>
    </>
  );
}
